// Copyright (c) 2012-2021 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved. NOTICE: All information contained herein
// is, and remains the property of GRAB. The intellectual and technical concepts contained herein are confidential, proprietary
// and controlled by GRAB and may be covered by patents, patents in process, and are protected by trade secret or copyright law.
//
// You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate, adapt or change this material
// in any way unless prior written permission is obtained from GRAB. Access to the source code contained herein is hereby
// forbidden to anyone except current GRAB employees or contractors with binding Confidentiality and Non-disclosure agreements
// explicitly covering such access.
//
// The copyright notice above does not evidence any actual or intended publication or disclosure of this source code,
// which includes information that is confidential and/or proprietary, and is a trade secret, of GRAB.
//
// ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR THROUGH USE OF THIS SOURCE
// CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE LAWS AND
// INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY
// OR IMPLY ANY RIGHTS TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING
// THAT IT MAY DESCRIBE, IN WHOLE OR IN PART.

import { BitSet } from './bitset'
import Emitter from 'mitt'

export class MultipleSelectionModel {
  private selectedIndices: BitSet = new BitSet()
  private updateLock: number = 0

  // last selected index
  private focusIndex: number = -1
  public readonly emitter = Emitter()

  constructor (private itemCount: number = 0, public selectionMode: 'Mulitple' | 'Single' = 'Mulitple') {
    this.itemCount = itemCount
  }

  public getItemCount () {
    return this.itemCount
  }

  public setItemCount (num: number) {
    this.itemCount = Math.max(0, num)
  }

  public getFocusIndex () {
    return this.focusIndex
  }

  public isAllSelected (): boolean {
    return this.selectedIndices.count() === this.itemCount && this.selectedIndices.count() !== 0
  }

  public select (index: number) {
    if (index >= this.itemCount || index < 0) {
      return
    }
    this.focusIndex = index
    const wasSelected = this.isSelected(index)
    if (this.selectionMode === 'Single') {
      this.selectedIndices.clear()
    }
    this.selectedIndices.set(index)

    if (!wasSelected) {
      this.fireChange()
    }
  }

  public selectIndices (...indices: number[]) {
    if (indices.length === 0) {
      return
    }
    if (this.selectionMode === 'Single') {
      this.selectedIndices.clear()
      this.select(indices[0])
      return
    }

    this.startBatch()
    indices.forEach(i => {
      this.select(i)
    })
    this.endBatch()
  }

  public selectRange (startIndex, endIndex) {
    if (this.selectionMode === 'Single' || this.itemCount < 0) {
      return
    }
    startIndex = Math.max(0, startIndex)
    endIndex = Math.min(endIndex, this.itemCount)
    for (let i = startIndex; i < endIndex; i++) {
      this.selectedIndices.set(i)
    }
    this.focusIndex = endIndex - 1
    this.fireChange()
  }

  public selectAll () {
    if (this.selectionMode === 'Single' || this.itemCount < 0) {
      return
    }
    this.focusIndex = this.itemCount - 1
    for (let i = 0; i < this.itemCount; i++) {
      this.selectedIndices.set(i)
    }
    this.fireChange()
  }

  public clear (index: number) {
    if (index >= this.itemCount || index < 0) {
      return
    }
    const wasSelected = this.selectedIndices.get(index)
    if (wasSelected) {
      this.selectedIndices.clear(index)
      this.focusIndex = this.selectedIndices.size
      this.fireChange()
    }
  }

  public clearAll () {
    this.selectedIndices.clear()
    this.focusIndex = -1
    this.fireChange()
  }

  public clearAndSelect (index: number) {
    if (index < 0 && index >= this.itemCount) {
      return
    }
    this.selectedIndices.clear()
    this.select(index)
  }

  public isSelected (index: number): boolean {
    if (index >= this.itemCount || index < 0) {
      return false
    }
    return this.selectedIndices.get(index)
  }

  public selectFirst () {
    if (this.itemCount > 0) {
      this.select(0)
    }
  }

  public selectLast () {
    if (this.itemCount > 0) {
      this.select(this.itemCount - 1)
    }
  }

  public selectPrevious () {
    if (this.focusIndex > 0) {
      this.select(this.focusIndex - 1)
    }
  }

  public selectNext () {
    if (this.focusIndex < this.itemCount - 1) {
      this.select(this.focusIndex + 1)
    }
  }

  public getSelectedIndices (): number[] {
    const indices = []
    for (let i = 0; i < this.itemCount; i++) {
      if (this.isSelected(i)) {
        indices.push(i)
      }
    }
    return indices
  }

  public startBatch () {
    this.updateLock++
  }

  public endBatch () {
    this.updateLock = Math.max(0, this.updateLock--)
    if (this.updateLock === 0) {
      this.fireChange()
    }
  }

  public dispose () {
    this.selectedIndices.clear()
    this.itemCount = 0
    this.focusIndex = -1
    this.emitter.all.clear()
  }

  private fireChange () {
    if (this.updateLock === 0) {
      this.emitter.emit('change', this.getSelectedIndices())
    }
  }
}
